import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Pencil, Save, X as XIcon, Plus as PlusIcon,
  ClipboardList, History, Trash2,
  Flag, GraduationCap, HeartHandshake, Lightbulb,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth, isAdmin } from "../contexts/AuthContext";
import { can } from "../lib/permissions";
import { AssessmentReview } from "./inventory/AssessmentReview";
import { AssessmentWorkspace } from "./inventory/AssessmentWorkspace";
import { StickyProgressBar } from "./inventory/StickyProgressBar";
import { ArchetypeDefine } from "./inventory/ArchetypeDefine";
import { StepHeading } from "./inventory/StepHeading";
import { CurrentRole } from "./coach-input/CurrentRole";
import { useSkillLibrary } from "../lib/inventorySkills";
import PlayerBadgesEditor from "./PlayerBadgesEditor";
import { selectionsFromTexts, textsFromSelections } from "../lib/inventoryOptions";

const DEFAULT_PERIOD = "Off Season 2026";
const SECTION_FIELDS = {
  character: ["character_elite", "character_needs_work"],
  inventory: ["key_performance_indicators", "current_role", "anchors", "rate_limiters", "development_priorities", "best_learning_environment", "relationship_connection", "helpful_tips", "inventory_selections", "completed_by", "inventory_notes"],
};

// Structured picks when saved that way; otherwise derive from the legacy free-text list (exact matches keep their category, the rest show as "Other").
const selectionsFor = (snap, field) => (snap.inventory_selections && snap.inventory_selections[field]) || selectionsFromTexts(field, snap[field]);


const EMPTY_SNAPSHOT = {
  period: DEFAULT_PERIOD,
  key_performance_indicators: [],
  current_role: [],
  anchors: [],
  rate_limiters: [],
  development_priorities: [],
  best_learning_environment: "",
  relationship_connection: "",
  helpful_tips: "",
  character_elite: [],
  character_needs_work: [],
  inventory_selections: null,
  completed_by: "",
  inventory_notes: "",
  updated_at: null,
};

export default function DevelopmentInventory({ playerId, playerName }) {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const showVibes = can(user, "inventory.vibes");
  const lib = useSkillLibrary();
  const [snapshot, setSnapshot] = useState(EMPTY_SNAPSHOT);
  const [draft, setDraft] = useState(EMPTY_SNAPSHOT);
  const [loading, setLoading] = useState(true);
  const [editSection, setEditSection] = useState(null); // "character" | "inventory" | null
  const [saving, setSaving] = useState(false);
  const [currentAssessment, setCurrentAssessment] = useState(null);
  const workspaceRef = useRef(null);
  const [versions, setVersions] = useState([]);
  const [viewing, setViewing] = useState({ character: null, inventory: null }); // version id being viewed, null = latest
  const [deleting, setDeleting] = useState(false);
  const editMode = editSection === "inventory";

  const versionsFor = (section) => versions.filter(v => v.section === section);
  const viewedVersion = (section) => viewing[section] ? versionsFor(section).find(v => v.id === viewing[section]) || null : null;
  // What's displayed: the latest snapshot, unless an older saved version is selected for that section.
  const shown = { ...snapshot, ...(viewedVersion("character")?.fields || {}), ...(viewedVersion("inventory")?.fields || {}) };

  const loadVersions = async () => {
    try { setVersions(await api.getSnapshotVersions(playerId)); } catch { setVersions([]); }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const s = await api.getSnapshot(playerId).catch(() => EMPTY_SNAPSHOT); // always the latest record
      if (cancelled) return;
      const merged = { ...EMPTY_SNAPSHOT, ...s };
      setSnapshot(merged);
      setDraft(merged);
      setLoading(false);
      loadVersions();
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerId]);

  const enterEdit = (section) => { setDraft(snapshot); setEditSection(section); };
  const setPicks = (field, sels) => setDraft(d => ({ ...d, [field]: textsFromSelections(sels), inventory_selections: { ...(d.inventory_selections || {}), [field]: sels } }));
  const cancelEdit = () => { setDraft(snapshot); setEditSection(null); };

  const saveSection = async (section) => {
    setSaving(true);
    try {
      const fields = Object.fromEntries(SECTION_FIELDS[section].map(k => [k, draft[k]]));
      const saved = await api.patchSnapshotSection(playerId, section, fields);
      const merged = { ...EMPTY_SNAPSHOT, ...saved };
      setSnapshot(merged);
      setDraft(merged);
      setEditSection(null);
      setViewing(v => ({ ...v, [section]: null }));
      loadVersions();
      toast.success(section === "character" ? "Character DNA saved as a new version" : "Development Inventory saved as a new version");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const deleteVersion = async (section) => {
    const v = viewedVersion(section);
    if (!v || !window.confirm(`Delete the ${fmtDate(v.saved_at)} version? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const res = await api.deleteSnapshotVersion(playerId, v.id);
      if (res?.snapshot) { const merged = { ...EMPTY_SNAPSHOT, ...res.snapshot }; setSnapshot(merged); setDraft(merged); }
      setViewing(s => ({ ...s, [section]: null }));
      await loadVersions();
      toast.success("Version deleted");
    } catch { toast.error("Delete failed"); } finally { setDeleting(false); }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : null;
  const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : null;

  const sectionDate = (section) => {
    const hasContent = SECTION_FIELDS[section].some(k => Array.isArray(snapshot[k]) ? snapshot[k].length : !!snapshot[k]);
    const iso = snapshot.section_updated_at?.[section] || (hasContent ? snapshot.updated_at : null);
    return fmtDate(iso);
  };


  if (loading) {
    return (
      <div className="flex justify-center items-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--org-accent)]" />
      </div>
    );
  }

  const SectionBar = ({ title, section, subtitle }) => {
    const active = editSection === section;
    const date = sectionDate(section);
    const list = versionsFor(section);
    const viewed = viewedVersion(section);
    const newestId = list[0]?.id || null;
    const selectValue = viewing[section] && viewing[section] !== newestId ? viewing[section] : "latest";
    return (
      <div className="space-y-2 pt-2" data-testid={`section-bar-${section}`}>
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.35em] text-[var(--org-accent)] font-bold">{title}</div>
            <div className="text-[10px] uppercase tracking-widest text-[#757575] mt-0.5">
              {subtitle ? `${subtitle} · ` : ""}{date ? `Updated ${date}` : "Not filled in yet"}
              {list.length > 0 && ` · ${list.length} saved ${list.length === 1 ? "version" : "versions"}`}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {list.length > 0 && !active && (
              <div className="flex items-center gap-1.5">
                <History className="w-3.5 h-3.5 text-[#757575]" />
                <select
                  value={selectValue}
                  onChange={(e) => { const id = e.target.value; setViewing(v => ({ ...v, [section]: (id === "latest" || id === newestId) ? null : id })); }}
                  className="bg-[#101010] border border-white/15 text-white text-[10px] uppercase tracking-[0.15em] px-2 py-2 focus:outline-none focus:border-[var(--org-accent)]"
                  data-testid={`history-select-${section}`}
                >
                  {list.map((v, i) => (
                    <option key={v.id} value={i === 0 ? "latest" : v.id}>
                      {i === 0 ? "Latest · " : ""}{fmtDateTime(v.saved_at)}{v.saved_by ? ` · ${v.saved_by}` : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {admin && !active && !viewed && list.length > 0 && (
              <button onClick={() => setViewing(v => ({ ...v, [section]: newestId }))} disabled={deleting} className="p-2 border border-white/15 text-[#757575] hover:text-red-400 hover:border-red-400/50 disabled:opacity-40" style={{ transition: "color 0.15s ease, border-color 0.15s ease" }} title="Delete the latest saved version" data-testid={`delete-latest-${section}-btn`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
            {admin && (active ? (
              <>
                <button onClick={cancelEdit} className="flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.25em] font-bold border border-white/20 text-[#a0a0a0] hover:text-white" style={{ transition: "color 0.15s ease" }} data-testid={`cancel-${section}-btn`}>
                  <XIcon className="w-3.5 h-3.5" /> Cancel
                </button>
                <button onClick={() => saveSection(section)} disabled={saving} className="flex items-center gap-2 px-4 py-2 text-[10px] uppercase tracking-[0.25em] font-bold bg-[var(--org-accent)] text-[#121212] hover:bg-[var(--org-accent-hover)] disabled:opacity-50" style={{ transition: "background-color 0.15s ease" }} data-testid={`save-${section}-btn`}>
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save {title}
                </button>
              </>
            ) : (
              !viewed && (
                <button onClick={() => enterEdit(section)} disabled={!!editSection} className="flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.25em] font-bold border border-white/20 text-white hover:border-[var(--org-accent)] hover:text-[var(--org-accent)] disabled:opacity-40" style={{ transition: "border-color 0.15s ease, color 0.15s ease" }} data-testid={`edit-${section}-btn`}>
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </button>
              )
            ))}
          </div>
        </div>
        {viewed && (
          <div className="flex items-center justify-between gap-3 flex-wrap border border-amber-400/40 bg-amber-400/10 px-4 py-2.5" data-testid={`viewing-version-${section}`}>
            <div className="text-[11px] text-amber-200">
              <span className="uppercase tracking-[0.2em] font-bold text-amber-400 text-[10px]">{viewed.id === newestId ? "Latest version · " : "Viewing saved version · "}</span>
              {fmtDateTime(viewed.saved_at)}{viewed.saved_by ? ` · by ${viewed.saved_by}` : ""}
            </div>
            <div className="flex items-center gap-2">
              {admin && (
                <button onClick={() => deleteVersion(section)} disabled={deleting} className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold border border-red-400/40 text-red-300 hover:bg-red-400/10 disabled:opacity-50" style={{ transition: "background-color 0.15s ease" }} data-testid={`delete-version-${section}-btn`}>
                  {deleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />} Delete version
                </button>
              )}
              <button onClick={() => setViewing(v => ({ ...v, [section]: null }))} className="px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold border border-white/20 text-white hover:border-[var(--org-accent)] hover:text-[var(--org-accent)]" style={{ transition: "border-color 0.15s ease, color 0.15s ease" }} data-testid={`back-to-latest-${section}-btn`}>
                Back to latest
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };


  return (
    <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8 pb-20 space-y-6" data-testid="development-inventory">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <ClipboardList className="w-5 h-5 text-[var(--org-accent)]" />
            <h2 className="font-heading text-2xl md:text-3xl uppercase tracking-tight text-white leading-none">
              Player Development Inventory
            </h2>
          </div>
          <div className="mt-1 h-[2px] w-16 bg-[var(--org-accent)]" />
          <div className="mt-3 text-[10px] uppercase tracking-widest text-[#757575]">Each section saves on its own · every save is kept as a dated version</div>
        </div>
        {!admin && <div className="text-[10px] uppercase tracking-widest text-[#757575] px-2 py-1 border border-white/10">Read-only</div>}
      </div>

      {/* ---- Steps 1–2: one assessment record covers every active library item (Physical Tools · Character DNA · Technical · Tactical) ---- */}
      {!lib.ready && <div className="text-[#757575] text-xs" data-testid="skill-library-loading">{lib.error || "Loading skill library…"}</div>}
      {lib.ready && <>
      <AssessmentWorkspace ref={workspaceRef} playerId={playerId} canEdit={admin} onCurrentChange={(doc) => setCurrentAssessment(doc)} />

      {/* ---- Step 3 · Anchors & Rate Limiters: auto-populated from Steps 1–2 ---- */}
      <StepHeading step={2} title="Review Anchors & Rate Limiters" subtitle="Fills in instantly from Step 1 · star 2–3 Anchors and 2–3 Rate Limiters per pillar · Strong / Mixed / At Risk per category" testid="heading-assessment-review" />
      <AssessmentReview assessment={currentAssessment} canEdit={admin && currentAssessment?.status !== "final"} onSetPrimary={(id) => workspaceRef.current?.setPrimary(id)} onCategoryRating={(cat, v) => workspaceRef.current?.setCategoryRating(cat, v)} />

      {/* ---- Step 3 · Define: Player Archetype (coach confirms; suggestions come from this assessment's ratings) ---- */}
      <StepHeading step={3} title="Player Archetype" subtitle="Offense and Defense · Primary (who they are most of the time) + optional Secondary · who they are as a player" testid="heading-archetype-define" />
      <ArchetypeDefine playerId={playerId} assessment={currentAssessment} canEdit={admin} onAssessmentChange={(doc) => workspaceRef.current?.replaceDoc(doc)} />

      {/* ---- Step 4 · Current Role: what the player's current team coach(es) ask of them ---- */}
      <StepHeading step={4} title="Current Role" subtitle="What the player's team asks of them · one entry per team · compared against the latest assessment and Step 3 archetype" testid="heading-current-role" />
      <CurrentRole playerId={playerId} admin={admin} />

      </>}

      <StickyProgressBar playerName={playerName} assessment={currentAssessment} />

      {/* Hidden for now at the user's request (2026-10-15): Feedback Report, legacy Character DNA reports, KPIs, Completed by, legacy Archetype & Role / Anchors blocks. Data untouched. */}
      {showVibes && <>
      <SectionBar title="Development Inventory" section="inventory" subtitle="Vibes" />

      {/* Hidden for now (2026-10-15): Fix Now strip, Development Priorities list and Notes card. Data untouched. */}
      <StepHeading step={5} title="Vibes" subtitle="Best learning environment · relationship & connection points · helpful tips" testid="heading-vibes" />

      {/* Bottom row: 3 short-text cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <TextCard
          title="Best Learning Environment"
          value={editMode ? draft.best_learning_environment : shown.best_learning_environment}
          editable={editMode}
          onChange={(v) => setDraft(d => ({ ...d, best_learning_environment: v }))}
          placeholder="e.g., Quick paced, live, no time to overthink"
          testid="card-learning-env"
          icon={GraduationCap}
        />
        <TextCard
          title="Relationship + Connection Points"
          value={editMode ? draft.relationship_connection : shown.relationship_connection}
          editable={editMode}
          onChange={(v) => setDraft(d => ({ ...d, relationship_connection: v }))}
          placeholder="e.g., Great listener, push verbal communication"
          testid="card-relationship"
          icon={HeartHandshake}
        />
        <TextCard
          title="Helpful Tips"
          value={editMode ? draft.helpful_tips : shown.helpful_tips}
          editable={editMode}
          onChange={(v) => setDraft(d => ({ ...d, helpful_tips: v }))}
          placeholder="e.g., Great kid, wants to work, open to any suggestions"
          testid="card-tips"
          icon={Lightbulb}
        />
      </div>
      </>}

      {/* Player Badges — player-level (not tied to a period), saved independently */}
      {admin && <PlayerBadgesEditor playerId={playerId} />}
    </main>
  );
}

export function ListCard({ title, subtitle, items, editable, onChange, testid, accent, dotColor, numbered, icon: Icon }) {
  const border = accent ? "border-[var(--org-accent)]/60" : "border-white/15";
  const titleColor = accent ? "text-[var(--org-accent)]" : "text-white";
  const iconColor = accent ? "#d8b854" : (dotColor || "#d8b854");
  const dot = dotColor || (accent ? "#d8b854" : "#d8b854");

  const updateItem = (idx, value) => {
    const next = [...items];
    next[idx] = value;
    onChange(next);
  };
  const removeItem = (idx) => onChange(items.filter((_, i) => i !== idx));
  const addItem = () => onChange([...items, ""]);

  const bulletFor = (i) => {
    if (numbered) {
      return (
        <span
          className="mt-0.5 shrink-0 inline-flex items-center justify-center w-6 h-6 font-heading text-sm text-[#121212] bg-[var(--org-accent)] rounded-sm"
        >
          {i + 1}
        </span>
      );
    }
    return <span className="mt-2 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: dot }} />;
  };

  return (
    <div className={`border ${border} bg-[#1c1c1c] p-5`} data-testid={testid}>
      <div className="border-b border-white/10 pb-3 mb-4">
        <div className="flex items-center gap-2.5">
          {Icon && <Icon className="w-4 h-4 shrink-0" style={{ color: iconColor }} />}
          <div className={`font-heading text-lg uppercase tracking-wide ${titleColor}`}>{title}</div>
        </div>
        <div className="text-[11px] italic text-[#a0a0a0] mt-1">{subtitle}</div>
      </div>
      {editable ? (
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i} className="flex items-start gap-2">
              {bulletFor(i)}
              <input
                value={it}
                onChange={(e) => updateItem(i, e.target.value)}
                placeholder="Add a bullet…"
                className="flex-1 bg-[#101010] border border-white/10 text-white text-sm px-3 py-2 focus:outline-none focus:border-[var(--org-accent)]"
                data-testid={`${testid}-input-${i}`}
              />
              <button
                onClick={() => removeItem(i)}
                className="mt-1 p-1 text-[#757575] hover:text-red-400"
                style={{ transition: "color 0.15s ease" }}
                data-testid={`${testid}-remove-${i}`}
              >
                <XIcon className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button
            onClick={addItem}
            className="flex items-center gap-2 mt-2 text-xs uppercase tracking-widest text-[var(--org-accent)] hover:text-[var(--org-accent-hover)]"
            style={{ transition: "color 0.15s ease" }}
            data-testid={`${testid}-add`}
          >
            <PlusIcon className="w-3.5 h-3.5" /> {numbered ? "Add KPI" : "Add bullet"}
          </button>
        </div>
      ) : (
        items.length === 0 ? (
          <div className="text-xs italic text-[#575757]">— Not filled in —</div>
        ) : (
          <ul className="space-y-2.5">
            {items.map((it, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-white">
                {bulletFor(i)}
                <span className="leading-relaxed pt-0.5">{it}</span>
              </li>
            ))}
          </ul>
        )
      )}
    </div>
  );
}

function TextCard({ title, value, editable, onChange, placeholder, testid, icon: Icon }) {
  return (
    <div className="border border-white/15 bg-[#1c1c1c] p-5" data-testid={testid}>
      <div className="border-b border-white/10 pb-3 mb-4">
        <div className="flex items-center gap-2.5">
          {Icon && <Icon className="w-4 h-4 shrink-0 text-[var(--org-accent)]" />}
          <div className="font-heading text-base uppercase tracking-wide text-white">{title}</div>
        </div>
      </div>
      {editable ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={4}
          className="w-full bg-[#101010] border border-white/10 text-white text-sm px-3 py-2 focus:outline-none focus:border-[var(--org-accent)] resize-none"
          data-testid={`${testid}-input`}
        />
      ) : (
        value ? (
          <div className="text-sm text-white leading-relaxed whitespace-pre-wrap">{value}</div>
        ) : (
          <div className="text-xs italic text-[#575757]">— Not filled in —</div>
        )
      )}
    </div>
  );
}
